import { useState, useEffect } from 'react';
import { useGetString } from '../../../hooks/useGetString';
import Link, { LinkProps } from 'next/link';
import { MouseEventHandler, useCallback } from 'react';
import { disabledClasses } from '../Button';

interface Props {
  bgColor?: string;
  borderColor?: string;
  textColor?: string;
  children: string | JSX.Element;
  href: LinkProps['href'];
  target?: '' | '_blank';
  color: 'red' | 'white' | 'gold' | 'darkGold' | 'grey' | 'redWhite';
  type?: 'submit' | 'reset' | 'button';
  disabled?: boolean;
  style?: string;
  onClick?: MouseEventHandler;
  dataTestId?: string;
}

export default function NavButton({
  onClick,
  href,
  target,
  color,
  disabled: propDisabled,
  type,
  children,
  style,
  dataTestId,
}: Props) {
  const [isLoading, setLoading] = useState(false);
  const disabled = propDisabled || isLoading;

  const getString = useGetString();
  const loadingString = getString('button.loading');

  const handleClick = (e) => {
    if (onClick) onClick(e);
    return setLoading(true);
  };
  const getClassByColor = useCallback((color: string): string => {
    switch (color) {
      case 'redWhite':
        return 'bg-redThree border-redThree text-white';
      case 'red':
        return 'bg-redFour border-darkGold text-darkGold';
      case 'white':
        return 'bg-white border-white text-black';
      case 'gold':
        return 'bg-darkerGold border-darkerGold text-white';
      case 'darkGold':
        return 'bg-redOne border-darkGold text-darkGold';
      case 'grey':
        return 'bg-white text-slate-400';
      default:
        return 'bg-redFour border-darkGold text-darkGold';
    }
  }, []);

  const getClassByDisabled = useCallback(
    (disabled: boolean): string => {
      if (disabled) {
        return `pointer-events-none border border-solid p-2 rounded-full block text-center min-h-[40px] leading-[20px] text-[1.25rem] font-bold uppercase w-full ${disabledClasses} ${
          style ? style : ''
        }`;
      }
      return `border border-solid p-2 rounded-full  block text-center min-h-[40px] leading-[20px] text-[1.25rem] font-[700] uppercase w-full ${
        style ? style : ''
      }`;
    },
    [style],
  );

  const classNames = `focus:outline-none focus-visible:ring-2 focus-visible:ring-darkGold focus-visible:ring-offset-2 ${getClassByColor(color)} ${getClassByDisabled(disabled)} w-full max-w-[460px] text-center`;

  if (href) {
    return (
      <Link href={href} passHref legacyBehavior>
        <a
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            onClick?.(e);
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            if (e.key === ' ' || e.key === 'Enter') {
              // Don't prevent default for Enter key as Next.js handles it
              if (e.key === ' ') {
                e.preventDefault();
                // Simulate a click for space key
                e.currentTarget.click();
              }
            }
          }}
          className={`${classNames} ${disabled ? 'pointer-events-none opacity-50' : ''}`}
          target={target}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          data-testid={dataTestId || ''}
        >
          {isLoading ? loadingString : children}
        </a>
      </Link>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      className={classNames}
      disabled={disabled}
      aria-disabled={disabled}
    >
      {isLoading ? loadingString : children}
    </button>
  );
}
